import React, { useEffect, useRef, useState } from 'react';
import {
  buildTestQrImageUrl,
} from './testQrScenarios';
import { TicketSuccessCard, buildTicketSuccessState } from './TicketSuccess';
import './ScannerSystemPresentationPageV2.css';

const PREVIEW_TIME_ZONE = 'America/Los_Angeles';

const formatPreviewDateTime = (isoDateTime) => {
  if (!isoDateTime) return '';
  const parsed = new Date(isoDateTime);
  if (Number.isNaN(parsed.getTime())) return String(isoDateTime);
  const formatted = new Intl.DateTimeFormat('en-US', {
    timeZone: PREVIEW_TIME_ZONE,
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(parsed);
  return `${formatted} PT`;
};

const AMP_DEMO = {
  code: 'TESTQR-PAC-AMP-SAT',
  checkInAt: '2026-06-20T17:42:11-07:00',
  result: {
    ticket_type: 'PAC AMP Saturday - PIT ROW AAAA SEAT 25',
    owner_name: 'Demo Guest - AMP SAT',
    owner_email: 'demo.amp.sat@ocmenafestival.com',
    is_amphitheater: true,
    section: 'PIT',
    row: 'AAAA',
    seat: '25',
    event_date: '2026-06-20',
    event_name: 'PAC AMP Saturday',
    scan_mode: 'both',
    status: 'SUCCESS',
    message: 'PAC AMP entry granted - one wristband issued',
  },
};

const FESTIVAL_DEMO = {
  code: 'TESTQR-FESTIVAL-VALID',
  checkInAt: '2026-06-19T11:08:02-07:00',
  result: {
    ticket_type: '3-Day Festival Pass',
    owner_name: 'Demo Guest - Festival',
    owner_email: 'demo.festival@ocmenafestival.com',
    total_days: 3,
    day_number: 1,
    event_date: '2026-06-19',
    event_name: 'OC MENA Festival',
    scan_mode: 'both',
    status: 'SUCCESS',
    message: 'Entry granted',
  },
};

const LIVE_METRICS = [
  { label: 'Validation Model', value: 'Deterministic Rule Engine', status: 'online' },
  { label: 'Scan Outcomes', value: 'Allow / Used / Blocked', status: 'online' },
  { label: 'Decision Scope', value: 'Ticket + Day + Status + Group', status: 'online' },
  { label: 'Auditability', value: 'Per-Scan Actor + Timestamp', status: 'online' },
];

const PIPELINE_STEPS = [
  { id: 1, title: 'Issuance + Ownership Lock', desc: 'Each ticket is tied to a specific owner with exact traceability.' },
  { id: 2, title: 'QR Identity + Validation', desc: 'Scanner resolves ticket code securely and checks active status.' },
  { id: 3, title: 'Day / Type Rules Applied', desc: 'PAC AMP gates, festival pass logic, vendor & parking rules enforced.' },
  { id: 4, title: 'Atomic Commit + Audit Log', desc: 'One commit per scan. Immutable actor + timestamp logging.' },
];

const SAFEGUARDS = [
  { title: 'Wrong-Day Protection', desc: 'PAC AMP and date-bound tickets are blocked on invalid event day attempts.', icon: '🛡️' },
  { title: 'Duplicate Prevention', desc: 'Already-used and same-pass-group same-day duplicate scans are denied.', icon: '🔒' },
  { title: 'Status Enforcement', desc: 'Voided, refunded, cancelled, or invalid states are blocked before entry.', icon: '⚡' },
  { title: 'Live Audit & Attendance', desc: 'Every scan attempt is logged and reflected in real-time attendance for full traceability and risk control.', icon: '�' },
  { title: 'Stateful Test Harness', desc: 'Deterministic and stateful training QR scenarios for staff onboarding.', icon: '🧪' },
  { title: 'Operational Recovery', desc: 'Controlled reset flows and robust logs for safe troubleshooting.', icon: '🔄' },
];

const SCENARIOS = [
  { code: 'TESTQR-PAC-AMP-SAT', lane: 'Core', status: 'VALID → SUCCESS', tone: 'ok', msg: 'PAC AMP Saturday check-in success.' },
  { code: 'TESTQR-PAC-AMP-SUN', lane: 'Core', status: 'VALID → SUCCESS', tone: 'ok', msg: 'PAC AMP Sunday check-in success.' },
  { code: 'TESTQR-FESTIVAL-VALID', lane: 'Core', status: 'VALID → SUCCESS', tone: 'ok', msg: 'Festival pass success (range June 19-21).' },
  { code: 'TESTQR-VENDOR-VALID', lane: 'Core', status: 'VALID → SUCCESS', tone: 'ok', msg: 'Vendor access success.' },
  { code: 'TESTQR-VENDOR-COMP-FEST', lane: 'Core', status: 'VALID → SUCCESS', tone: 'ok', msg: 'Vendor comp festival pass success.' },
  { code: 'TESTQR-PARKING-VALID', lane: 'Core', status: 'VALID → SUCCESS', tone: 'ok', msg: 'VIP parking pass success.' },
  { code: 'TESTQR-VOIDED-REFUNDED', lane: 'Edge', status: 'VOIDED', tone: 'blocked', msg: 'Blocked: voided/refunded ticket.' },
  { code: 'TESTQR-ALREADY-USED', lane: 'Edge', status: 'ALREADY_USED', tone: 'used', msg: 'Already used screen with timestamp details.' },
  { code: 'TESTQR-FLEX-SAME-DAY-BLOCK', lane: 'Edge', status: 'ALREADY_USED', tone: 'used', msg: 'Same-group same-day block for multi-day passes.' },
  { code: 'TESTQR-WRONG-DAY', lane: 'Edge', status: 'WRONG_DAY', tone: 'blocked', msg: 'Wrong date block with valid day guidance.' },
];

const buildDemoState = (demo) =>
  buildTicketSuccessState(
    {
      ...demo.result,
      scan_event_date: demo.result.event_date,
    },
    { checkInTime: formatPreviewDateTime(demo.checkInAt) }
  );

function useInView(threshold = 0.12) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setInView(true);
      },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, inView];
}

const Section = ({ children, className = '' }) => {
  const [ref, inView] = useInView();
  return (
    <section ref={ref} className={`ssv2-section ${inView ? 'ssv2-visible' : 'ssv2-hidden'} ${className}`}>
      {children}
    </section>
  );
};

const ScannerSystemPresentationPageV2 = () => {
  const ampTicket = buildDemoState(AMP_DEMO);
  const festTicket = buildDemoState(FESTIVAL_DEMO);

  return (
    <div className="ssv2-page">
      <div className="ssv2-grid-bg" aria-hidden="true" />
      <div className="ssv2-scanline" aria-hidden="true" />

      <header className="ssv2-topbar">
        <div className="ssv2-topbar-inner">
          <div className="ssv2-brand">
            <img src="/logo.webp" alt="OC MENA Festival" className="ssv2-brand-logo" />
            <div>
              <div className="ssv2-brand-title">OC MENA FESTIVAL</div>
              <div className="ssv2-brand-sub">Ticketing Integrity Command</div>
            </div>
          </div>
          <div className="ssv2-topbar-right">
            <span className="ssv2-pulse-dot" />
            <span className="ssv2-status-text">SYSTEM ONLINE</span>
          </div>
        </div>
      </header>

      <main className="ssv2-main">
        <Section className="ssv2-hero">
          <div className="ssv2-hero-content">
            <div className="ssv2-hero-eyebrow">Scanner + Ticketing Intelligence</div>
            <h1 className="ssv2-hero-title">
              <span className="ssv2-gradient-text">Mission Control</span> for Gate Entry
            </h1>
            <p className="ssv2-hero-lead">
              A real-time, deterministic rule engine that protects revenue, prevents duplicate entry,
              and keeps every gate decision fast, explainable, and auditable under real-world pressure.
            </p>
            <div className="ssv2-hero-actions">
              <a href="https://blue.ocmenafestival.com" target="_blank" rel="noopener noreferrer" className="ssv2-btn ssv2-btn--primary">
                Launch Blue Gate Scanner
              </a>
              <a href="https://green.ocmenafestival.com" target="_blank" rel="noopener noreferrer" className="ssv2-btn ssv2-btn--primary">
                Launch Green Gate Scanner
              </a>
              <a href="https://pacamp.ocmenafestival.com" target="_blank" rel="noopener noreferrer" className="ssv2-btn ssv2-btn--primary">
                Launch PAC AMP Gate Scanner
              </a>
              <a href="/qr-test" target="_blank" rel="noopener noreferrer" className="ssv2-btn ssv2-btn--ghost">
                Public QR Test
              </a>
              <a href="/admin?tab=test-qr-codes" target="_blank" rel="noopener noreferrer" className="ssv2-btn ssv2-btn--ghost">
                Admin QR Lab
              </a>
            </div>
          </div>
          <div className="ssv2-hero-glow" aria-hidden="true" />
        </Section>

        <Section>
          <div className="ssv2-metrics">
            {LIVE_METRICS.map((m) => (
              <div className="ssv2-metric" key={m.label}>
                <div className="ssv2-metric-top">
                  <span className="ssv2-metric-status">
                    <span className="ssv2-pulse-dot ssv2-pulse-dot--sm" />
                    {m.status.toUpperCase()}
                  </span>
                </div>
                <div className="ssv2-metric-value">{m.value}</div>
                <div className="ssv2-metric-label">{m.label}</div>
              </div>
            ))}
          </div>
        </Section>

        <Section>
          <div className="ssv2-pipeline">
            <div className="ssv2-section-header">
              <div className="ssv2-section-num">01</div>
              <h2 className="ssv2-section-title">Ticketing Control Pipeline</h2>
            </div>
            <div className="ssv2-pipeline-track">
              {PIPELINE_STEPS.map((step, idx) => (
                <React.Fragment key={step.id}>
                  <div className="ssv2-pipeline-node">
                    <div className="ssv2-pipeline-ring">
                      <span>{step.id}</span>
                    </div>
                    <div className="ssv2-pipeline-text">
                      <h3>{step.title}</h3>
                      <p>{step.desc}</p>
                    </div>
                  </div>
                  {idx < PIPELINE_STEPS.length - 1 && (
                    <div className="ssv2-pipeline-connector" aria-hidden="true">
                      <div className="ssv2-pipeline-beam" />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </Section>

        <Section>
          <div className="ssv2-terminals">
            <div className="ssv2-section-header">
              <div className="ssv2-section-num">02</div>
              <h2 className="ssv2-section-title">Live Scan Terminals</h2>
            </div>
            <p className="ssv2-section-subtitle">
              Scan these QR codes with the live scanner to see real validation outcomes.
            </p>
            <div className="ssv2-terminals-grid">
              <div className="ssv2-terminal">
                <div className="ssv2-terminal-header">
                  <span className="ssv2-terminal-dot" />
                  <span className="ssv2-terminal-label">AMP STATION — SATURDAY</span>
                  <span className="ssv2-terminal-id">ID: AMP-SAT-01</span>
                </div>
                <div className="ssv2-terminal-body">
                  <div className="ssv2-terminal-top">
                    <div className="ssv2-qr-wrap">
                      <img
                        src={buildTestQrImageUrl(AMP_DEMO.code, 260)}
                        alt="AMP Saturday demo QR"
                        className="ssv2-qr-img"
                        loading="lazy"
                      />
                      <div className="ssv2-qr-caption">{AMP_DEMO.code}</div>
                    </div>
                    <div className="ssv2-terminal-meta">
                      <div className="ssv2-terminal-meta-title">Station Configuration</div>
                      <div className="ssv2-terminal-meta-grid">
                        <div><span>Queue</span><strong>PAC AMP SAT</strong></div>
                        <div><span>Ticket Class</span><strong>Amphitheater</strong></div>
                        <div><span>Policy</span><strong>Single scan • 1 wristband</strong></div>
                        <div><span>Example Seat</span><strong>PIT / AAAA / 25</strong></div>
                      </div>
                    </div>
                  </div>
                  <div className="ssv2-preview-wrap">
                    <div className="ssv2-preview-badge">Expected Result Preview</div>
                    <TicketSuccessCard ticketData={ampTicket} preview compact />
                  </div>
                </div>
              </div>

              <div className="ssv2-terminal">
                <div className="ssv2-terminal-header">
                  <span className="ssv2-terminal-dot ssv2-terminal-dot--fest" />
                  <span className="ssv2-terminal-label">FESTIVAL STATION — 3-DAY PASS</span>
                  <span className="ssv2-terminal-id">ID: FEST-3D-01</span>
                </div>
                <div className="ssv2-terminal-body">
                  <div className="ssv2-terminal-top">
                    <div className="ssv2-qr-wrap">
                      <img
                        src={buildTestQrImageUrl(FESTIVAL_DEMO.code, 260)}
                        alt="Festival demo QR"
                        className="ssv2-qr-img"
                        loading="lazy"
                      />
                      <div className="ssv2-qr-caption">{FESTIVAL_DEMO.code}</div>
                    </div>
                    <div className="ssv2-terminal-meta">
                      <div className="ssv2-terminal-meta-title">Station Configuration</div>
                      <div className="ssv2-terminal-meta-grid">
                        <div><span>Queue</span><strong>Festival Main</strong></div>
                        <div><span>Ticket Class</span><strong>3-Day Festival Pass</strong></div>
                        <div><span>Rule</span><strong>One scan per pass group/day</strong></div>
                        <div><span>Date Window</span><strong>June 19–21, 2026</strong></div>
                      </div>
                    </div>
                  </div>
                  <div className="ssv2-preview-wrap">
                    <div className="ssv2-preview-badge ssv2-preview-badge--fest">Expected Result Preview</div>
                    <TicketSuccessCard ticketData={festTicket} preview compact />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Section>

        <Section>
          <div className="ssv2-safeguards">
            <div className="ssv2-section-header">
              <div className="ssv2-section-num">03</div>
              <h2 className="ssv2-section-title">Safeguards & Risk Controls</h2>
            </div>
            <div className="ssv2-safeguards-grid">
              {SAFEGUARDS.map((s) => (
                <div className="ssv2-safeguard" key={s.title}>
                  <div className="ssv2-safeguard-icon">{s.icon}</div>
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </Section>

        <Section>
          <div className="ssv2-scenarios">
            <div className="ssv2-section-header">
              <div className="ssv2-section-num">04</div>
              <h2 className="ssv2-section-title">Scenario Matrix</h2>
            </div>
            <p className="ssv2-section-subtitle">
              Deterministic outcomes used for staff training and edge-case validation.
            </p>
            <div className="ssv2-scenarios-table">
              {SCENARIOS.map((sc) => (
                <div className={`ssv2-scenarios-row ssv2-scenarios-row--${sc.tone}`} key={sc.code}>
                  <div className="ssv2-scenarios-cell ssv2-scenarios-cell--code">
                    <span className="ssv2-scenarios-pill">{sc.lane}</span>
                    <code>{sc.code}</code>
                  </div>
                  <div className="ssv2-scenarios-cell ssv2-scenarios-cell--status">{sc.status}</div>
                  <div className="ssv2-scenarios-cell ssv2-scenarios-cell--msg">{sc.msg}</div>
                </div>
              ))}
            </div>
          </div>
        </Section>

        <footer className="ssv2-footer">
          <div className="ssv2-footer-inner">
            <span>OC MENA Festival — Scanner Integrity System</span>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default ScannerSystemPresentationPageV2;
